import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { buildAuthHref } from "@/lib/utm";
import { trackCtaClick } from "@/lib/analytics";

type ButtonVariant = React.ComponentProps<typeof Button>["variant"];
type ButtonSize = React.ComponentProps<typeof Button>["size"];

interface CampaignCtaProps {
  /** Stable CTA identifier, e.g. "agencies-hero". Sent as src= and GA4 location. */
  location: string;
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

/**
 * Trial CTA used across the campaign landing pages. On click it:
 *   - builds the /auth?mode=signup&src=<location> link, forwarding inbound
 *     utm and gclid params via buildAuthHref();
 *   - fires the GA4 cta_click event { location, destination };
 *   - navigates client-side (no full reload, preserves SPA state).
 *
 * It never fires a Google Ads conversion — sign-up conversion is owned by
 * PrivateRoute on the trial confirmation path, not the landing pages.
 */
const CampaignCta = ({
  location,
  children,
  variant,
  size = "lg",
  className,
}: CampaignCtaProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    const href = buildAuthHref(location);
    trackCtaClick(location, href);
    navigate(href);
  };

  return (
    <Button variant={variant} size={size} className={className} onClick={handleClick}>
      {children}
    </Button>
  );
};

export default CampaignCta;
